import React, { FC, useEffect, useMemo, useState } from 'react';
import { RouteComponentProps } from 'react-router';
import { useSelector } from 'react-redux';
import { Modal } from '@eika/modal';
import { Icon, IconTypes } from '@eika/icon';
import { StandardButton } from '@eika/button';
import { RichIconTypes } from '@eika/rich-icon';
import Card from '@eika/card/src/Card';
import { Link } from '@eika/link';
import CustomCard from '../../../../../common/Card/Card';
import { getPageAppending } from '../../../../../common/util/Functions';
import './SwitchSavingProfilePage.scss';
import Grid from '../../../../../common/Grid/Grid';
import { HeaderLink, Spinner } from '../../../../../common/share/ShareComponents';
import AppMessage from '../../../../../common/AppMessage/AppMessage';
import { State } from '../../../../../domain/State';
import ProductInfoCard from '../../../../../common/ExtendedStyleguide/ProductInfoCard';
import { RouterConfig } from '../../../../../app/Router';
import { useStorebandInfo } from '../../../../../common/util/Hook';
import { SelectableProfile } from '../../../../../domain/Storebrand';
import SelectableProfileFundAllocation from './SelectableProfileFundAllocation';
import SavingProfilesEstimatesModal from '../estimates/SavingProfilesEstimatesModal';
import { useTranslate } from '../../../../../hooks/useTranslate';

export const URL_KEY_SWITCH = 'switch';
export const URL_KEY_CONFIRM_SWITCH = 'confirm-switch';

const SwitchSavingProfilePage: FC<RouteComponentProps<{ id?: string }> & RouterConfig> = (props) => {
    const appState = useSelector((state: State) => state?.App);
    const storebrandState = useSelector((state: State) => state?.Storebrand);
    useStorebandInfo({ Storebrand: storebrandState }, props.action);

    const translate = useTranslate();

    const agreementId = useMemo(() => props.match.params.id || '', [props.match.params.id]);

    const availableProfiles = useMemo(
        () =>
            (storebrandState?.info?.[agreementId]?.selectableProfiles || []).sort(
                (a: SelectableProfile, b: SelectableProfile) =>
                    (b.fundAllocationPercentage || 0) - (a.fundAllocationPercentage || 0)
            ),
        [storebrandState, agreementId]
    );
    const appending = useMemo(() => getPageAppending(props), []);
    const [loading, setLoading] = useState(true);
    const [modalOpen, setModalOpen] = useState<false | 'self-composed' | 'estimate'>(false);

    useEffect(() => {
        if (availableProfiles.length) setLoading(false);
    }, [availableProfiles]);

    const modalCard = (
        <CustomCard
            type="spare-link-panel"
            click={(e) => {
                e?.preventDefault();
                setModalOpen('estimate');
            }}
        >
            <>
                <Icon type={IconTypes.GRAFLINJE_36} />
                <h3>{translate('compareSavingProfiles')}</h3>
                <article>{translate('compareSavingProfilesDescription')}</article>
            </>
        </CustomCard>
    );

    return (
        <div className={`switch-profile-page ${appending || 'none-appending'}`}>
            <Grid
                namespace="sparing-top"
                list={[
                    {
                        layout: 'twelve',
                        element: <HeaderLink length={1} text="Spareprofiler" />,
                    },
                ]}
            />

            <Grid
                namespace="sparing-header"
                list={[
                    {
                        layout: 'twelve',
                        element: <h1>{translate('changeProfile')}</h1>,
                    },
                ]}
            />

            <Grid
                list={[
                    {
                        layout: 'seven',
                        element: (
                            <>
                                <AppMessage appMessage={appState?.serviceMessage} context="STOREBRAND_RISK_PROFILE" />
                                <article>{translate('savingProfilesListDescription')}</article>

                                <div className="only-for-mobile">{modalCard}</div>
                                <div className="group">
                                    <h3>{translate('chooseSavingProfile')}</h3>

                                    {!loading && (
                                        <div className="selectable-profiles">
                                            {availableProfiles.map((profile: SelectableProfile, i: number) => (
                                                <div className="component-group" key={profile.isin}>
                                                    <ProductInfoCard
                                                        highlightedNote={i === 0 ? 'Vi anbefaler' : undefined}
                                                        label={profile.name}
                                                    >
                                                        <SelectableProfileFundAllocation
                                                            fundAllocationPercentage={profile.fundAllocationPercentage}
                                                        />
                                                        <Link href={`${location.href}/${profile.isin}`}>
                                                            {translate('description')}
                                                        </Link>
                                                        <Link
                                                            linkType="button-positive"
                                                            href={`${location.href.replace(
                                                                URL_KEY_SWITCH,
                                                                URL_KEY_CONFIRM_SWITCH
                                                            )}/${profile.isin}`}
                                                        >
                                                            {translate('select')}
                                                        </Link>
                                                    </ProductInfoCard>
                                                </div>
                                            ))}
                                            <div className="component-group">
                                                <ProductInfoCard label={translate('selfComposedSavingProfile')}>
                                                    <article>{translate('selfComposedSavingProfileDescription')}</article>
                                                    <StandardButton
                                                        size="small"
                                                        buttonType="positive"
                                                        onClick={() => setModalOpen('self-composed')}
                                                    >
                                                        {translate('select')}
                                                    </StandardButton>
                                                </ProductInfoCard>
                                            </div>
                                        </div>
                                    )}

                                    {loading && <Spinner type="linkPanel" namespace="top-spaced" />}
                                </div>
                            </>
                        ),
                    },
                    {
                        layout: 'four',
                        element: <div className="only-for-desktop">{modalCard}</div>,
                    },
                ]}
            />
            <Modal onClose={() => setModalOpen(false)} show={modalOpen === 'self-composed'}>
                <Card title={translate('selfComposedSavingProfile')} isModal icon={RichIconTypes.HAAND_HEIHEI}>
                    <article>
                        {translate('selfComposedSavingProfileDescriptionExtended')}
                    </article>
                    <div className="icon-after-button-wrapper">
                        <StandardButton
                            size="large"
                            buttonType="positive"
                            icon={IconTypes.EKSTERN_24}
                            onClick={() => {
                                window.location.href =
                                    'https://www.storebrand.no/min-sparing/partner/forside?partnerId=eika';
                            }}
                        >
                            {translate('goToStorebrand')}
                        </StandardButton>
                    </div>
                </Card>
            </Modal>
            <Modal onClose={() => setModalOpen(false)} show={modalOpen === 'estimate'} size="wide">
                <SavingProfilesEstimatesModal profiles={availableProfiles} />
            </Modal>
        </div>
    );
};

export default SwitchSavingProfilePage;
